import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ── Types ──────────────────────────────────────────────────────────

export type MarketingPersona =
  | "executive_leadership"
  | "technical_leadership"
  | "operations_leadership"
  | "quality_regulatory_leadership"
  | "research_leadership"
  | "clinical_leadership";

export type BuyerSegment =
  | "EXECUTIVE"
  | "TECHNICAL"
  | "OPERATIONS"
  | "QUALITY_REGULATORY"
  | "RESEARCH"
  | "CLINICAL"
  | "NOT_ICP";

export type OutreachStatus =
  | "not_started"
  | "queued"
  | "contacted"
  | "responded"
  | "meeting_scheduled"
  | "converted"
  | "opted_out";

export const MARKETING_PERSONAS: MarketingPersona[] = [
  "executive_leadership",
  "technical_leadership",
  "operations_leadership",
  "quality_regulatory_leadership",
  "research_leadership",
  "clinical_leadership",
];

// Back-compat alias: `BUYING_PERSONAS` / `BuyingPersona` is legacy naming for the
// buyer-segment classification. New code should prefer `BUYER_SEGMENTS` / `BuyerSegment`.
export type BuyingPersona = BuyerSegment;

export const BUYER_SEGMENTS: BuyerSegment[] = [
  "EXECUTIVE",
  "TECHNICAL",
  "OPERATIONS",
  "QUALITY_REGULATORY",
  "RESEARCH",
  "CLINICAL",
];

export const BUYING_PERSONAS: BuyerSegment[] = BUYER_SEGMENTS;

export const OUTREACH_STATUSES: { value: OutreachStatus; label: string }[] = [
  { value: "not_started", label: "Not Started" },
  { value: "queued", label: "Queued" },
  { value: "contacted", label: "Contacted" },
  { value: "responded", label: "Responded" },
  { value: "meeting_scheduled", label: "Meeting Scheduled" },
  { value: "converted", label: "Converted" },
  { value: "opted_out", label: "Opted Out" },
];

export interface ContactDetail {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  job_title: string | null;
  department: string | null;
  seniority_level: string | null;
  buyer_segment: BuyerSegment | null;
  marketing_persona: MarketingPersona | null;
  is_decision_maker: boolean;
  outreach_status: OutreachStatus;
  last_contacted_at: string | null;
  next_followup_date: string | null;
  next_followup_action: string | null;
  last_engagement_type: string | null;
  last_engagement_date: string | null;
  contact_notes: string | null;
  notes: string | null;
  tags: string[] | null;
  location: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  source: string | null;
  company_id: string | null;
  company_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface EngagementTimelineEvent {
  event_id: string;
  event_type: "outreach" | "campaign_enrollment" | "opportunity";
  event_subtype: string;
  event_title: string;
  event_detail: string | null;
  event_date: string;
  event_status: string | null;
  campaign_name: string | null;
  contact_id: string;
  company_id: string | null;
}

export interface ContactCampaign {
  id: string;
  campaign_id: string;
  campaign_name: string;
  engagement_status: string;
  enrolled_at: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  replied_at: string | null;
  reply_sentiment: string | null;
  outcome: string | null;
  meeting_booked: boolean;
  messages_sent_count: number;
}

export interface ContactOpportunity {
  id: string;
  opportunity_id: string;
  opportunity_name: string;
  buying_role: string | null;
  relationship_strength: string | null;
  is_primary: boolean;
  notes: string | null;
}

// ── Contact Detail Hook ────────────────────────────────────────────

export function useContactDetail(contactId: string | null) {
  return useQuery({
    queryKey: ["contact-detail", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("contacts")
        .select("*, companies:company_id(company_name)")
        .eq("id", contactId)
        .single();
      if (error) throw error;

      return {
        ...data,
        company_name: data.companies?.company_name || data.company_name || null,
        companies: undefined,
      } as ContactDetail;
    },
  });
}

// ── Engagement Timeline Hook ───────────────────────────────────────

export function useContactEngagementTimeline(contactId: string | null) {
  return useQuery({
    queryKey: ["contact-engagement-timeline", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_contact_engagement_timeline")
        .select("*")
        .eq("contact_id", contactId)
        .order("event_date", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data || []) as EngagementTimelineEvent[];
    },
  });
}

// ── Contact Campaigns Hook ─────────────────────────────────────────

export function useContactCampaigns(contactId: string | null) {
  return useQuery({
    queryKey: ["contact-campaigns", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("campaign_contacts")
        .select(
          "id, campaign_id, engagement_status, enrolled_at, sent_at, accepted_at, replied_at, reply_sentiment, outcome, meeting_booked, messages_sent_count, campaigns!inner(name)"
        )
        .eq("contact_id", contactId)
        .order("enrolled_at", { ascending: false });
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.id,
        campaign_id: row.campaign_id,
        campaign_name: row.campaigns?.name || "Campaign",
        engagement_status: row.engagement_status,
        enrolled_at: row.enrolled_at,
        sent_at: row.sent_at,
        accepted_at: row.accepted_at,
        replied_at: row.replied_at,
        reply_sentiment: row.reply_sentiment,
        outcome: row.outcome,
        meeting_booked: row.meeting_booked,
        messages_sent_count: row.messages_sent_count,
      })) as ContactCampaign[];
    },
  });
}

// ── Contact Opportunities Hook ─────────────────────────────────────

export function useContactOpportunities(contactId: string | null) {
  return useQuery({
    queryKey: ["contact-opportunities", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("opportunity_contacts")
        .select(
          "id, opportunity_id, buying_role, relationship_strength, is_primary, notes, pipeline_opportunities!inner(name)"
        )
        .eq("contact_id", contactId);
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.id,
        opportunity_id: row.opportunity_id,
        opportunity_name: row.pipeline_opportunities?.name || "Opportunity",
        buying_role: row.buying_role,
        relationship_strength: row.relationship_strength,
        is_primary: row.is_primary,
        notes: row.notes,
      })) as ContactOpportunity[];
    },
  });
}

// ── Update Contact Mutation ────────────────────────────────────────

export interface UpdateContactInput {
  marketing_persona?: MarketingPersona | null;
  buyer_segment?: BuyerSegment | null;
  is_decision_maker?: boolean;
  outreach_status?: OutreachStatus;
  next_followup_date?: string | null;
  next_followup_action?: string | null;
  contact_notes?: string | null;
  tags?: string[] | null;
}

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contactId,
      updates,
    }: {
      contactId: string;
      updates: UpdateContactInput;
    }) => {
      const { error } = await (supabase as any)
        .from("contacts")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", contactId);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["contact-detail", variables.contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ["contact-detail-full", variables.contactId],
      });
      queryClient.invalidateQueries({ queryKey: ["account-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["account-top-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["task-center-list"] });
      queryClient.invalidateQueries({ queryKey: ["task-center-stats"] });
    },
  });
}
